import React, { useState } from 'react';
import { Box } from '@chakra-ui/react';
import TableHeaderCell from './TableHeaderCell';
import TableRow from './TableRow';
import TableActions from './TableActions';
import Pagination from './Pagination';

const DEFAULT_PAGE_DETAILS = { page: 1, total: 10, perpage: 10 };

/**
 * @param {Object} props
 * @param {Array} props.rows
 * @param {Array} props.headers
 * @param {Object} props.rowActions
 * @param {Object} props.tableActions
 * @param {Object} props.pageDetails
 * @param {(page: number) => void} [props.onPageChange]
 * @param {(perPage: number) => void} [props.onPerPageChange]
 * @param {string} [props.noDataMessage] // <-- add prop
 */
const ZaraTable = ({
  rows = [],
  headers = [],
  rowActions = {},
  tableActions = {},
  pageDetails = {},
  onPageChange = (/* page */) => { },
  onPerPageChange = (/* perPage */) => { },
  noDataMessage = '', // <-- add prop
}) => {
  const [currentSortKey, setCurrentSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState('none'); // 'asc', 'desc', 'none'
  const [selectedRowObjects, setSelectedRowObjects] = useState(new Set());

  const showCheckboxes = !!(tableActions && tableActions.delete && tableActions.delete.action);

  const handleSort = (headerKey) => {
    if (currentSortKey === headerKey) {
      setSortDirection(prevDirection => {
        if (prevDirection === 'asc') return 'desc';
        // If 'desc', cycle back to 'asc'. If it were 'none', it would go 'asc' -> 'desc' -> 'none'
        // For simplicity per instructions: 'asc' -> 'desc' -> 'asc'
        return 'asc';
      });
    } else {
      setCurrentSortKey(headerKey);
      setSortDirection('asc');
    }
  };

  // Sorting logic exists but is not currently used
  // const sortedRows = useMemo(() => {
  //   if (sortDirection === 'none' || !currentSortKey) {
  //     return rows;
  //   }
  //   const sorted = [...rows].sort((a, b) => {
  //     const valA = a[currentSortKey];
  //     const valB = b[currentSortKey];
  //
  //     if (valA < valB) {
  //       return sortDirection === 'asc' ? -1 : 1;
  //     }
  //     if (valA > valB) {
  //       return sortDirection === 'asc' ? 1 : -1;
  //     }
  //     return 0;
  //   });
  //   return sorted;
  // }, [rows, currentSortKey, sortDirection]);

  const mergedPageDetails = {
    ...DEFAULT_PAGE_DETAILS,
    ...pageDetails,
    total: pageDetails.total ?? rows.length,
  };

  const { page, perpage, total } = mergedPageDetails;

  const paginatedRows = rows;

  const handleCheckboxChange = (rowObject) => {
    setSelectedRowObjects(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(rowObject)) {
        newSelected.delete(rowObject);
      } else {
        newSelected.add(rowObject);
      }
      return newSelected;
    });
  };

  const handleTableAction = (actionName) => {
    if (actionName === 'add' && tableActions.add && tableActions.add.action) {
      tableActions.add.action();
    } else if (actionName === 'delete' && tableActions.delete && tableActions.delete.action) {
      tableActions.delete.action(Array.from(selectedRowObjects));
      setSelectedRowObjects(new Set());
    }
  };

  if (!rows || rows.length === 0) {
    return (
      <div
        style={{
          borderRadius: "12px",
          padding: "12px",
          maxWidth: "100%",
          background: "#fff",
          border: "none",
          overflow: "auto",
          whiteSpace: "nowrap"
        }}
      >
        {Object.keys(tableActions).length > 0 && (
          <TableActions
            tableActions={tableActions}
            selectedRowCount={selectedRowObjects.size}
            onTableAction={handleTableAction}
          />
        )}
        <Box mt={8} p={8} textAlign="center" color="gray.500" borderWidth={1} borderRadius="md">
          {noDataMessage && noDataMessage.trim() ? noDataMessage : 'No data Found'}
        </Box>
      </div>
    );
  }

  return (
    <div
      style={{
        borderRadius: "12px",
        padding: "12px",
        maxWidth: "100%",
        background: "#fff",
        border: "none",
        overflow: "auto",
        whiteSpace: "nowrap"
      }}
    >
      {Object.keys(tableActions).length > 0 && (
        <TableActions
          tableActions={tableActions}
          selectedRowCount={selectedRowObjects.size}
          onTableAction={handleTableAction}
        />
      )}
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            {showCheckboxes && <th style={{ width: '50px', padding: 0 }}></th>}
            {headers.map(header => (
              <TableHeaderCell
                key={header.key}
                header={header}
                onSort={handleSort}
                currentSortKey={currentSortKey}
                currentSortDirection={sortDirection}
              />
            ))}
            {Object.keys(rowActions).length > 0 && (
              <th
                style={{
                  color: "#71717a",
                  padding: 0,
                  fontWeight: 500,
                  fontSize: 16,
                  fontFamily: "inherit"
                }}
              >
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {paginatedRows.map((row, index) => (
            <TableRow
              key={row.id || index}
              rowIndex={index}
              row={row}
              headers={headers}
              rowActions={rowActions}
              showCheckbox={showCheckboxes}
              isSelected={selectedRowObjects.has(row)}
              onCheckboxChange={() => handleCheckboxChange(row)}
            />
          ))}
        </tbody>
      </table>
      <Pagination
        page={page}
        total={total}
        perpage={perpage}
        onPageChange={onPageChange}
        onPerPageChange={onPerPageChange}
      />
    </div>
  );
};

export default ZaraTable;
